import React, { useState, useCallback, useMemo } from 'react';
import { Entity } from '../types';
import EntityTypeSelector from './EntityTypeSelector';
import { getEntityColorClass, parseLabelColor } from '../utils/labelColor';

interface AnnotationCardProps {
  exampleText: string;
  annotations: Entity[];
  availableEntityTypes: string[];
  onAnnotationsChange: (annotations: Entity[]) => void;
  customLabelColors?: Record<string, string>;
}

interface EditState {
  index: number;
  position: { top: number; left: number };
}

const AnnotationCard: React.FC<AnnotationCardProps> = ({
  exampleText,
  annotations,
  availableEntityTypes,
  onAnnotationsChange,
  customLabelColors
}) => {
  const [editState, setEditState] = useState<EditState | null>(null);

  // Sort annotations by start position for the side list
  const sortedAnnotations = useMemo(() => {
    return [...annotations].sort((a, b) => (a.start || 0) - (b.start || 0));
  }, [annotations]);

  const handleEntityTypeSelect = useCallback((entityType: string) => {
    if (editState !== null) {
      const updated = [...annotations];
      updated[editState.index] = {
        ...updated[editState.index],
        label: entityType
      };
      onAnnotationsChange(updated);
    }
    setEditState(null);
  }, [editState, annotations, onAnnotationsChange]);

  const handleCancel = useCallback(() => {
    setEditState(null);
  }, []);

  const handleDeleteAnnotation = useCallback((index: number) => {
    const updatedAnnotations = annotations.filter((_, i) => i !== index);
    onAnnotationsChange(updatedAnnotations);
  }, [annotations, onAnnotationsChange]);

  const handleEditAnnotation = useCallback((index: number, event?: React.MouseEvent) => {
    const annotation = annotations[index];
    if (!annotation) return;

    let top = 200;
    let left = 200;
    if (event) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      top = rect.bottom + 5;
      left = rect.left;
    }
    setEditState({ index, position: { top, left } });
  }, [annotations]);

  // Reference passive context-only; exampleText is intentionally unused now
  // that inline span rendering has moved to <EntitySpans> in ComparisonView.
  void exampleText;

  return (
    <div className="p-3 rounded-lg border bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
      {/* Header */}
      <div className="mb-2">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900 flex items-center">
            <svg className="w-4 h-4 mr-1 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Annotations
          </h4>
          <span className="text-xs text-gray-600">
            {annotations.length} {annotations.length === 1 ? 'entity' : 'entities'}
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-1">
          Select text in the example above to annotate
        </p>
      </div>

      {/* Entity List */}
      {annotations.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-medium text-gray-700">Annotations:</div>
          <div className="space-y-1">
            {sortedAnnotations.map((entity, idx) => {
              // Find the original index in the unsorted annotations array
              const originalIndex = annotations.findIndex(
                a => a.text === entity.text &&
                     a.label === entity.label &&
                     a.start === entity.start &&
                     a.end === entity.end
              );

              const colorClass = getEntityColorClass(entity.label, customLabelColors);
              const isCustomColor = colorClass.includes('|') || colorClass.startsWith('#') || colorClass.startsWith('rgb');
              const parsed = isCustomColor ? parseLabelColor(colorClass) : null;

              return (
                <div
                  key={`entity-list-${entity.start}-${entity.end}-${entity.text}-${entity.label}-${idx}`}
                  className="flex items-center justify-between text-xs bg-white rounded p-2 border border-purple-100"
                >
                  <div className="flex-1 flex items-center space-x-2">
                    <span
                      className={isCustomColor ? 'inline-block px-2 py-1 rounded font-mono font-medium text-xs border-2' : `${colorClass} px-2 py-1 rounded font-mono`}
                      style={parsed ? {
                        backgroundColor: parsed.bg,
                        color: parsed.text,
                        borderColor: parsed.border
                      } : undefined}
                    >
                      {entity.text}
                    </span>
                    <span className="text-gray-400">→</span>
                    <span className="font-medium text-gray-700">{entity.label}</span>
                    {entity.start !== undefined && entity.end !== undefined && (
                      <span className="text-gray-400 text-xs">
                        ({entity.start}, {entity.end})
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-1 ml-2">
                    <button
                      onClick={(e) => handleEditAnnotation(originalIndex, e)}
                      className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                      title="Edit label"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteAnnotation(originalIndex)}
                      className="p-1 text-red-600 hover:bg-red-50 rounded"
                      title="Delete"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Entity Type Selector Popup (side-list edit) */}
      {editState !== null && (
        <EntityTypeSelector
          availableTypes={availableEntityTypes}
          onSelect={handleEntityTypeSelect}
          onCancel={handleCancel}
          position={editState.position}
          initialValue={annotations[editState.index]?.label}
        />
      )}
    </div>
  );
};

export default AnnotationCard;
